import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n/I18nContext';
import { ProgressRadar } from '../components/Radar';
import { ShareWheel } from '../components/ShareWheel';
import { Progress } from '../components/ui/progress';
import { Lock, Check, Play, NotebookPen, Award, Target, Globe, Sparkles, ArrowRight, Swords } from 'lucide-react';

const spring = { type: 'spring', stiffness: 300, damping: 20 };

export default function YouthHome() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [radar, setRadar] = useState(null);
  const [summary, setSummary] = useState(null);
  const [badges, setBadges] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const navigate = useNavigate();
  const { t } = useI18n();

  useEffect(() => {
    Promise.all([
      api.get('/chapters'), api.get('/progress/radar'), api.get('/progress/summary'), api.get('/badges'),
    ]).then(([c, r, s, b]) => {
      setData(c.data);
      setRadar(r.data);
      setSummary(s.data);
      setBadges(b.data.badges);
    }).catch(() => {});
    api.get('/exchange/my-challenges').then((r) => setChallenges(r.data.challenges)).catch(() => {});
  }, []);

  const translateRadar = (items) => (items || []).map((r) => ({ ...r, axis: t(`axes.${r.key}`, undefined, r.axis) }));

  const lowestAxis = radar?.radar?.length
    ? [...radar.radar].sort((a, b) => a.value - b.value)[0]
    : null;

  const acts = [];
  (data?.chapters || []).forEach((ch) => {
    let act = acts.find((a) => a.name === ch.act);
    if (!act) { act = { name: ch.act, chapters: [] }; acts.push(act); }
    act.chapters.push(ch);
  });

  const stats = summary ? [
    { icon: Play, label: t('youth.statChapters'), value: `${summary.chapters_done}/${summary.chapters_total}`, testid: 'stat-chapters' },
    { icon: Sparkles, label: t('youth.statExperiences'), value: `${summary.experiences_done}/${summary.experiences_total}`, testid: 'stat-experiences' },
    { icon: Target, label: t('youth.statMissions'), value: summary.missions_done, testid: 'stat-missions' },
    { icon: Globe, label: 'Give Back', value: summary.givebacks_done, testid: 'stat-givebacks' },
    { icon: NotebookPen, label: t('youth.statCarnet'), value: summary.carnet_notes, testid: 'stat-carnet' },
  ] : [];

  return (
    <div data-testid="youth-dashboard">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={spring}
        className="relative rounded-3xl overflow-hidden ring-1 ring-or-400/20">
        <img src="/images/eliano_stade_clean.jpg" alt="" aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-left" />
        <div className="absolute inset-0 bg-gradient-to-r from-nuit-950/85 via-nuit-950/70 to-nuit-950/45" />
        <div className="relative p-8 sm:p-10">
        <p className="text-or-400 font-bold tracking-widest text-sm uppercase">{t('youth.eyebrow')}</p>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-creme mt-2">
          {t('youth.welcome', { name: user?.name })}
        </h1>
        <p className="text-base sm:text-lg text-brume mt-2">{t('youth.question')}</p>
        {summary && (
          <div className="mt-6 max-w-xl">
            <div className="flex justify-between text-sm font-bold text-creme mb-2">
              <span>{summary.overall_percent}{t('youth.progressLabel')}</span>
              <span data-testid="current-chapter">{t('youth.chapterProgress', { current: summary.next_chapter ? summary.next_chapter - 1 : 28 })}</span>
            </div>
            <Progress value={summary.overall_percent} className="h-3 rounded-full bg-nuit-700" data-testid="overall-progress" />
          </div>
        )}
        {lowestAxis && summary && summary.overall_percent > 0 && (
          <p className="text-sm text-or-300 font-bold mt-4" data-testid="weekly-suggestion">
            {t('youth.weeklySuggestion', { axis: t(`axes.${lowestAxis.key}`, undefined, lowestAxis.axis).toLowerCase() })}
          </p>
        )}
        </div>
      </motion.div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-10">
        {stats.map((s, i) => (
          <motion.div key={s.testid} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ ...spring, delay: i * 0.05 }}
            className="bg-nuit-800 border border-nuit-700 rounded-3xl p-5 text-center" data-testid={s.testid}>
            <s.icon className="w-6 h-6 text-or-400 mx-auto mb-2" strokeWidth={2.5} />
            <p className="font-fredoka font-semibold text-2xl text-creme">{s.value}</p>
            <p className="text-xs font-bold text-brume mt-1">{s.label}</p>
          </motion.div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-8 mt-10">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ ...spring, delay: 0.1 }}
          className="space-y-6">
          {summary?.next_chapter && (
            <button onClick={() => navigate(`/chapitre/${summary.next_chapter}`)} data-testid="continue-adventure-button"
              className="w-full text-left bg-or-400 hover:bg-or-300 rounded-3xl p-6 transition-all hover:-translate-y-1 group">
              <p className="font-bold text-nuit-900 text-sm uppercase tracking-wider">{t('youth.continue')}</p>
              <p className="font-fredoka font-semibold text-2xl text-nuit-950 mt-2 flex items-center justify-between">
                {t('common.chapterN', { n: summary.next_chapter })}
                <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" strokeWidth={3} />
              </p>
              <p className="text-nuit-800 font-semibold text-sm mt-1">
                {data?.chapters?.find((c) => c.number === summary.next_chapter)?.title}
              </p>
            </button>
          )}
          <div className="bg-nuit-800 border border-nuit-700 rounded-3xl p-6">
            <h2 className="text-xl font-semibold text-creme mb-2">{t('youth.myRadar')}</h2>
            <ProgressRadar data={translateRadar(radar?.radar)} testid="youth-radar" />
            <ShareWheel data={translateRadar(radar?.radar)} percent={radar?.overall_percent} testid="share-wheel-btn" />
          </div>
          <Link to="/carnet" data-testid="go-diary-link">
            <div className="flex items-center gap-3 bg-[#fdfbf7] rounded-2xl p-4 hover:-translate-y-0.5 hover:shadow-lg transition-all cursor-pointer">
              <NotebookPen className="w-6 h-6 text-amber-800" strokeWidth={2.5} />
              <div>
                <p className="font-bold text-slate-800 text-sm">{t('youth.myDiary')}</p>
                <p className="text-xs text-slate-500">{t('youth.myDiarySub')}</p>
              </div>
            </div>
          </Link>
          <Link to="/mon-parcours" data-testid="go-synthesis-link">
            <div className="flex items-center gap-3 bg-nuit-800 border border-nuit-700 rounded-2xl p-4 hover:-translate-y-0.5 hover:border-or-400/40 transition-all cursor-pointer">
              <Sparkles className="w-6 h-6 text-or-400" strokeWidth={2.5} />
              <div>
                <p className="font-bold text-creme text-sm">{t('youth.myJourney')}</p>
                <p className="text-xs text-brume">{t('youth.myJourneySub')}</p>
              </div>
            </div>
          </Link>
          {challenges.length > 0 && (
            <div className="bg-nuit-800 border border-or-400/30 rounded-3xl p-6" data-testid="youth-challenges">
              <h2 className="text-xl font-semibold text-creme mb-4 flex items-center gap-2">
                <Swords className="w-5 h-5 text-or-400" strokeWidth={2.5} /> {t('youth.challengesTitle')}
              </h2>
              <div className="space-y-3">
                {challenges.map((c) => (
                  <div key={c.challenge_id} data-testid={`youth-challenge-${c.challenge_id}`}
                    className="bg-nuit-900 border border-nuit-700 rounded-2xl p-4">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <p className="text-xs font-bold text-brume">{t('youth.challengeFrom', { name: c.adult_name })}</p>
                      <span className={`text-xs font-bold px-3 py-1 rounded-full ${c.status === 'done' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-or-400/15 text-or-300'}`}
                        data-testid={`youth-challenge-status-${c.challenge_id}`}>
                        {c.status === 'done' ? t('youth.challengeStatusDone') : t('youth.challengeStatusLaunched')}
                      </span>
                    </div>
                    <p className="font-bold text-creme text-sm mt-2">{c.title}</p>
                    <p className="text-sm text-brume mt-1">{c.text}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>

        <div className="lg:col-span-2 space-y-10">
          <div className="bg-nuit-800 border border-nuit-700 rounded-3xl p-6">
            <h2 className="text-xl font-semibold text-creme mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-or-400" strokeWidth={2.5} /> {t('youth.myBadges')}
            </h2>
            <div className="flex flex-wrap gap-3" data-testid="badges-list">
              {badges.map((b) => (
                <span key={b.id} data-testid={`badge-${b.id}`} title={t(`badges.${b.id}.hint`, undefined, b.hint)}
                  className={`px-4 py-2 rounded-full text-xs font-bold border transition-all
                    ${b.earned ? 'bg-or-400 text-nuit-950 border-or-400' : 'bg-nuit-900 text-brume border-nuit-600'}`}>
                  {t(`badges.${b.id}.label`, undefined, b.label)}
                </span>
              ))}
            </div>
          </div>

          {acts.map((act) => (
            <div key={act.name}>
              <h2 className="text-lg font-semibold text-or-400 mb-4" data-testid={`act-${act.name}`}>{act.name}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-5">
                {act.chapters.map((ch, i) => {
                  const locked = ch.status === 'locked';
                  const done = ch.status === 'completed';
                  return (
                    <motion.button key={ch.number} data-testid={`chapter-card-${ch.number}`}
                      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ ...spring, delay: Math.min(i * 0.04, 0.5) }}
                      disabled={locked}
                      onClick={() => navigate(`/chapitre/${ch.number}`)}
                      className={`text-left rounded-3xl p-5 min-h-[150px] flex flex-col justify-between border transition-all
                        ${locked ? 'bg-nuit-900 border-nuit-800 opacity-50 cursor-not-allowed'
                          : done ? 'bg-nuit-700 border-or-400/50 hover:-translate-y-1 hover:gold-glow cursor-pointer'
                          : 'bg-nuit-800 border-nuit-600 hover:-translate-y-1 hover:border-or-400/60 hover:gold-glow cursor-pointer'}`}>
                      <div className="flex items-start justify-between">
                        <span className={`font-fredoka font-semibold text-2xl ${locked ? 'text-brume' : 'text-or-400'}`}>{ch.number}</span>
                        {locked && <Lock className="w-5 h-5 text-brume" strokeWidth={2.5} />}
                        {done && <span className="w-7 h-7 rounded-full bg-or-400 flex items-center justify-center"><Check className="w-4 h-4 text-nuit-950" strokeWidth={3} /></span>}
                        {ch.status === 'unlocked' && <span className="w-7 h-7 rounded-full bg-nuit-600 flex items-center justify-center"><Play className="w-4 h-4 text-or-300" strokeWidth={3} /></span>}
                      </div>
                      <div>
                        <p className={`font-bold text-sm leading-snug mt-3 ${locked ? 'text-brume' : 'text-creme'}`}>{ch.title}</p>
                        {ch.subtitle && <p className="text-xs text-brume mt-1 italic line-clamp-2">{ch.subtitle}</p>}
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
